import React, { useEffect, useMemo, useState } from 'react'
import '../styles/Form.css'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDarkMode } from '../utils/DarkModeProvider'
import { useAuth } from './AuthContext'
import { ScaleLoader } from 'react-spinners'
import Swal from 'sweetalert2'
import stylesTableDarkMode from '../styles/TableDarkMode'
import stylesTableLightMode from '../styles/TableLightMode'

export default function ResumenInstancias() {
    const location = useLocation()
    const navigate = useNavigate()
    const { darkMode, toggleDarkMode } = useDarkMode()
    const { user, token } = useAuth()
    const dynamicStyles = darkMode ? stylesTableDarkMode : stylesTableLightMode

    const usuarioNavegacion = location.state?.usuario
    const usuarioActual = usuarioNavegacion || user
    const esAdmin = usuarioActual?.rol === 'Admin'
    const esOperador = usuarioActual?.rol === 'Operador'
    const [estadoCargaCerrada, setEstadoCargaCerrada] = useState(location.state?.bloquearEdicion === true)

    const [nombreDocente, setNombreDocente] = useState(location.state?.nombreDocente || '')
    const [portadaDocente, setPortadaDocente] = useState(null)
    const [puntajeDocente, setPuntajeDocente] = useState(null)
    const evaluacion = location.state?.evaluacion

    const [instancias, setInstancias] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [gruposColapsados, setGruposColapsados] = useState({})
    const [finalizandoCarga, setFinalizandoCarga] = useState(false)
    const [exportandoPdf, setExportandoPdf] = useState(false)

    useEffect(() => {
        if (!usuarioActual?.id || !token || !evaluacion?.id) {
            setLoading(false)
            return
        }

        fetch(`${process.env.REACT_APP_API_URL}/item-instancias/${usuarioActual.id}/${evaluacion.id}/`, {
            method: 'GET',
            headers: {
                'accept': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        })
            .then(r => {
                if (!r.ok) throw new Error('Error al cargar los items')
                return r.json()
            })
            .then(data => setInstancias(Array.isArray(data) ? data : []))
            .catch(err => setError(err.message))
            .finally(() => setLoading(false))
            }, [usuarioActual?.id, token, evaluacion?.id])

    useEffect(() => {
        const nombreDesdeNavegacion = location.state?.nombreDocente
        if (nombreDesdeNavegacion) {
            setNombreDocente(nombreDesdeNavegacion)
        }

        if (!usuarioActual?.id || !token || !evaluacion?.id) {
            return
        }

        fetch(`${process.env.REACT_APP_API_URL}/docente-portada/${usuarioActual.id}/${evaluacion.id}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('No se pudo cargar la portada del docente')
                }
                return response.json()
            })
            .then((docente) => {
                setPortadaDocente(docente)
                const estadoActual = String(docente?.estado_actual || '').trim().toLowerCase()
                setEstadoCargaCerrada(estadoActual === 'carga_cerrada')
                if (!nombreDesdeNavegacion) {
                    const nombreCompleto = `${docente?.nombre || ''} ${docente?.apellido || ''}`.trim()
                    if (nombreCompleto) {
                        setNombreDocente(nombreCompleto)
                    }
                }
            })
            .catch((nombreError) => {
                console.log(nombreError)
            })
    }, [location.state?.nombreDocente, usuarioActual?.id, token, evaluacion?.id])

    useEffect(() => {
        if (!usuarioActual?.id || !token || !evaluacion?.id) {
            setPuntajeDocente(null)
            return
        }

        fetch(`${process.env.REACT_APP_API_URL}/docente-puntaje/${usuarioActual.id}/${evaluacion.id}/`, {
            method: 'GET',
            headers: {
                accept: 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('No se pudo cargar el puntaje del docente')
                }
                return response.json()
            })
            .then((puntaje) => {
                setPuntajeDocente({
                    obtenido: Number(puntaje?.puntaje_obtenido_hasta_el_momento || 0),
                    objetivo: Number(puntaje?.puntaje_a_obtener || 0)
                })
            })
            .catch((puntajeError) => {
                console.log(puntajeError)
                setPuntajeDocente(null)
            })
    }, [usuarioActual?.id, token, evaluacion?.id])

    const instanciasNormalizadas = useMemo(() => (
        instancias.map((instancia) => ({
            id: instancia.id,
            indiceCompleto: instancia.item?.indice_completo || '-',
            nombreItem: instancia.item?.item?.nombre || 'Sin nombre',
            estado: instancia.estado?.estado || 'sin estado',
            atributos: Array.isArray(instancia.atributos)
                ? instancia.atributos.map((atributo) => ({
                    id: atributo.id,
                    nombre: atributo.atributo?.nombre || 'Atributo',
                    tipo: atributo.atributo?.tipo || null,
                    valor: atributo.valor || '-'
                }))
                : []
        }))
    ), [instancias])

    const textoDocumentoDocente = useMemo(() => {
        const tipo = String(portadaDocente?.tipo_documento || '').trim()
        const numero = String(portadaDocente?.documento || '').trim()
        if (tipo && numero) return `${tipo} ${numero}`
        if (tipo) return tipo
        if (numero) return numero
        return ''
    }, [portadaDocente?.tipo_documento, portadaDocente?.documento])

    const normalizarHref = (valor) => {
        if (typeof valor !== 'string') return ''
        const limpio = valor.trim()
        if (!limpio || limpio === '-') return ''
        if (/^https?:\/\//i.test(limpio)) return limpio
        return `https://${limpio}`
    }

    const compararIndices = (indiceA, indiceB) => {
        const partesA = String(indiceA).split('.').map((parte) => Number(parte))
        const partesB = String(indiceB).split('.').map((parte) => Number(parte))
        const maxLength = Math.max(partesA.length, partesB.length)

        for (let index = 0; index < maxLength; index += 1) {
            const valorA = Number.isNaN(partesA[index]) ? -1 : (partesA[index] ?? -1)
            const valorB = Number.isNaN(partesB[index]) ? -1 : (partesB[index] ?? -1)

            if (valorA !== valorB) {
                return valorA - valorB
            }
        }

        return 0
    }

    const gruposOrdenados = useMemo(() => {
        const gruposPorIndice = instanciasNormalizadas
            .reduce((acumulador, instancia) => {
                const grupoExistente = acumulador.get(instancia.indiceCompleto)

                if (grupoExistente) {
                    grupoExistente.instancias.push(instancia)
                    return acumulador
                }

                acumulador.set(instancia.indiceCompleto, {
                    indiceCompleto: instancia.indiceCompleto,
                    nombreItem: instancia.nombreItem,
                    instancias: [instancia]
                })

                return acumulador
            }, new Map())

        return Array.from(gruposPorIndice.values())
            .sort((grupoA, grupoB) => compararIndices(grupoA.indiceCompleto, grupoB.indiceCompleto))
    }, [instanciasNormalizadas])

    useEffect(() => {
        setGruposColapsados((prevState) => {
            const nextState = { ...prevState }
            let changed = false

            gruposOrdenados.forEach((grupo) => {
                if (typeof nextState[grupo.indiceCompleto] === 'undefined') {
                    nextState[grupo.indiceCompleto] = false
                    changed = true
                }
            })

            Object.keys(nextState).forEach((indiceCompleto) => {
                if (!gruposOrdenados.some((grupo) => grupo.indiceCompleto === indiceCompleto)) {
                    delete nextState[indiceCompleto]
                    changed = true
                }
            })

            return changed ? nextState : prevState
        })
    }, [gruposOrdenados])

    const totalInstancias = instanciasNormalizadas.length

    const estadosConfirmados = new Set([
        'confirmado',
        'confirmado_admin',
        'confirmado_por_docente',
        'confirmado_por_admin',
        'creado_por_admin',
        'modificado_por_admin'
    ])

    const estadosPendientes = new Set([
        'creado_por_docente',
        'modificado_por_docente'
    ])

    const estaConfirmada = (estado) => estadosConfirmados.has(String(estado || '').toLowerCase())
    const estaPendiente = (estado) => estadosPendientes.has(String(estado || '').toLowerCase())

    const totalConfirmadas = instanciasNormalizadas.filter((inst) => estaConfirmada(inst.estado)).length
    const totalPendientes = instanciasNormalizadas.filter((inst) => estaPendiente(inst.estado)).length
    const totalItemsDistintos = gruposOrdenados.length

    const formatearPeriodo = () => {
        if (!evaluacion?.fecha_inicio || !evaluacion?.fecha_fin) return 'Período no especificado'

        const [anioInicio, mesInicio, diaInicio] = evaluacion.fecha_inicio.split('-')
        const [anioFin, mesFin, diaFin] = evaluacion.fecha_fin.split('-')
        return `${diaInicio}/${mesInicio}/${anioInicio} - ${diaFin}/${mesFin}/${anioFin}`
    }

    const formatearFechaCorte = (fecha) => {
        if (!fecha || typeof fecha !== 'string') return '-'
        if (fecha.includes('-')) {
            const [anio, mes, dia] = fecha.split('-')
            if (anio && mes && dia) {
                return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${anio}`
            }
        }
        if (fecha.includes('/')) {
            const [dia, mes, anio] = fecha.split('/')
            if (dia && mes && anio) {
                return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${anio}`
            }
        }
        return fecha
    }

    const tieneSituacionRevista = Boolean(
        portadaDocente?.fecha_corte
        || portadaDocente?.unidad_academica_nombre
        || portadaDocente?.situacion_de_revista
        || portadaDocente?.categoria
        || portadaDocente?.funcion_nombre
    )

    const sanitizarSegmentoArchivo = (valor, fallback = '', { lowercase = true } = {}) => {
        let base = String(valor || '').trim()

        if (lowercase) {
            base = base.toLowerCase()
        }

        base = base
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^A-Za-z0-9-]+/g, '_')
            .replace(/_+/g, '_')
            .replace(/^_+|_+$/g, '')

        return base || fallback
    }

    const capitalizarPalabras = (valor) => String(valor || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map((parte) => parte.charAt(0).toUpperCase() + parte.slice(1).toLowerCase())
        .join(' ')

    const construirNombreArchivoPDF = () => {
        const anioInicio = String(evaluacion?.fecha_inicio || '').slice(0, 4)
        const anioFin = String(evaluacion?.fecha_fin || '').slice(0, 4)
        const periodoBase = (anioInicio && anioFin)
            ? (anioInicio === anioFin ? anioInicio : `${anioInicio}-${anioFin}`)
            : (evaluacion?.descripcion || 'periodo')

        let nombre = String(portadaDocente?.nombre || '').trim()
        let apellido = String(portadaDocente?.apellido || '').trim()

        if ((!nombre || !apellido) && nombreDocente) {
            const partes = String(nombreDocente).trim().split(/\s+/)
            if (partes.length > 0) {
                const apellidoInferido = partes[partes.length - 1]
                const nombreInferido = partes.slice(0, -1).join(' ')

                if (!apellido) apellido = apellidoInferido
                if (!nombre) nombre = nombreInferido || partes[0]
            }
        }

        const periodo = sanitizarSegmentoArchivo(periodoBase, 'periodo')
        const apellidoSanitizado = sanitizarSegmentoArchivo(capitalizarPalabras(apellido), 'Apellido', { lowercase: false })
        const nombreSanitizado = sanitizarSegmentoArchivo(capitalizarPalabras(nombre), 'Nombre', { lowercase: false })

        return `Evaluacion_Docente_${periodo}_${apellidoSanitizado}_${nombreSanitizado}.pdf`
    }

    const getEstadoStyles = (estado) => {
        const estadoNormalizado = String(estado || '').toLowerCase()

        if (estadosConfirmados.has(estadoNormalizado)) {
            if (estadoNormalizado.includes('admin')) {
                return styles.estadoConfirmadoAdmin
            }
            return styles.estadoConfirmado
        }

        return styles.estadoPendiente
    }

    const toggleGrupo = (indiceCompleto) => {
        setGruposColapsados((prevState) => ({
            ...prevState,
            [indiceCompleto]: !prevState[indiceCompleto]
        }))
    }

    const estanTodosColapsados = gruposOrdenados.length > 0
        && gruposOrdenados.every((grupo) => gruposColapsados[grupo.indiceCompleto])

    const toggleTodosLosGrupos = () => {
        const proximoEstado = !estanTodosColapsados

        setGruposColapsados(
            gruposOrdenados.reduce((acumulador, grupo) => {
                acumulador[grupo.indiceCompleto] = proximoEstado
                return acumulador
            }, {})
        )
    }

    const descargarDocumento = async (idDocumento, nombreDocumento) => {
        if (!usuarioActual?.id || !evaluacion?.id || !token) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo descargar el documento.',
                confirmButtonText: 'Cerrar',
                confirmButtonColor: '#972034'
            })
            return
        }

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/docente-portada/${usuarioActual.id}/${evaluacion.id}/documentos/${idDocumento}/descargar/`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            )

            if (!response.ok) {
                throw new Error('Error al descargar el documento')
            }

            const blob = await response.blob()
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = nombreDocumento || 'documento'
            document.body.appendChild(link)
            link.click()
            window.URL.revokeObjectURL(url)
            document.body.removeChild(link)
        } catch (downloadError) {
            console.log(downloadError)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo descargar el documento.',
                confirmButtonText: 'Cerrar',
                confirmButtonColor: '#972034'
            })
        }
    }

    const volverAFormulario = () => {
        if (esOperador) {
            return
        }

        navigate('/formulario', {
            state: { evaluacion, usuario: usuarioActual }
        })
    }

    const volverAlHome = () => {
        navigate('/home', {
            replace: true,
            state: { usuario: usuarioActual }
        })
    }

    const finalizarCarga = async () => {
        const confirmacion = await Swal.fire({
            title: '¿Finalizar carga?',
            text: 'Una vez finalizada la carga, no podrá volver a ingresar al formulario.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Aceptar',
            cancelButtonText: 'Cancelar',
            background: darkMode ? '#1e1e1e' : '#fff',
            color: darkMode ? '#fff' : '#545454',
            confirmButtonColor: '#2f7d4f',
            cancelButtonColor: darkMode ? '#e57373' : '#d9534f'
        })

        if (!confirmacion.isConfirmed) {
            return
        }

        const hayPendientes = instanciasNormalizadas.some(
            (inst) => estaPendiente(inst.estado)
        )

        if (hayPendientes) {
            Swal.fire({
                icon: 'warning',
                title: 'No se puede finalizar',
                text: 'Todos los items tienen que estar confirmados para finalizar la carga',
                confirmButtonText: 'Cerrar',
                confirmButtonColor: '#972034'
            })
            return
        }

        if (!usuarioActual?.id || !evaluacion?.id || !token) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo finalizar la carga.',
                confirmButtonText: 'Cerrar',
                confirmButtonColor: '#972034'
            })
            return
        }

        setFinalizandoCarga(true)
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/docente-estado/transicionar-carga-cerrada/${usuarioActual.id}/${evaluacion.id}/`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                }
            )

            if (!response.ok) {
                const responseText = await response.text()
                let backendMessage = responseText

                try {
                    const parsed = JSON.parse(responseText)
                    backendMessage = parsed?.detail || parsed?.message || responseText
                } catch {
                    backendMessage = responseText
                }

                const normalizedMessage = String(backendMessage).toLowerCase()
                const transitionErrorText = "no se puede transicionar del estado 'carga_cerrada' al estado 'carga_cerrada"

                if (normalizedMessage.includes(transitionErrorText)) {
                    setEstadoCargaCerrada(true)
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'El usuario ya finalizó la carga',
                        confirmButtonText: 'Cerrar',
                        confirmButtonColor: '#972034'
                    })
                    return
                }

                throw new Error('No se pudo finalizar la carga')
            }

            setEstadoCargaCerrada(true)

            Swal.fire({
                icon: 'success',
                title: 'Carga finalizada',
                html: '<strong>El informe de evaluación se ha entregado correctamente.</strong><br/>Será evaluado por una Comisión Evaluadora Externa. Una vez finalizado el proceso de evaluación, el resultado estará disponible en MiUNQ.',
                showConfirmButton: true,
                confirmButtonText: 'Cerrar',
                confirmButtonColor: '#972034'
            })
        } catch (finalizarError) {
            console.log(finalizarError)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo finalizar la carga.',
                confirmButtonText: 'Cerrar',
                confirmButtonColor: '#972034'
            })
        } finally {
            setFinalizandoCarga(false)
        }
    }

    const exportarResumenAPDF = async () => {
        const cargarScriptCDN = (id, src) => new Promise((resolve, reject) => {
            const existente = document.getElementById(id)
            if (existente) {
                if (existente.dataset.loaded === 'true') {
                    resolve()
                    return
                }
                existente.addEventListener('load', () => resolve(), { once: true })
                existente.addEventListener('error', () => reject(new Error(`No se pudo cargar ${id}`)), { once: true })
                return
            }

            const script = document.createElement('script')
            script.id = id
            script.src = src
            script.async = true
            script.onload = () => {
                script.dataset.loaded = 'true'
                resolve()
            }
            script.onerror = () => reject(new Error(`No se pudo cargar ${id}`))
            document.body.appendChild(script)
        })

        setExportandoPdf(true)
        try {
            await cargarScriptCDN('jspdf-cdn-script', 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js')
            await cargarScriptCDN('jspdf-autotable-cdn-script', 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js')

            const jsPDF = window.jspdf?.jsPDF
            if (!jsPDF) {
                throw new Error('No se pudo inicializar jsPDF')
            }

            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            })

            const pageWidth = doc.internal.pageSize.getWidth()
            const pageHeight = doc.internal.pageSize.getHeight()
            const printableMarginX = 14
            const printableMarginTop = 8
            const printableMarginBottom = 12
            const contentWidth = pageWidth - (printableMarginX * 2)

            const periodoTexto = formatearPeriodo()

            const puntajeObtenido = Number(puntajeDocente?.obtenido || 0)
            const puntajeObjetivo = Number(puntajeDocente?.objetivo || 0)

            // Cabecera del PDF
            doc.setFillColor(151, 32, 52)
            doc.roundedRect(printableMarginX, printableMarginTop, contentWidth, 24, 2, 2, 'F')
            doc.setTextColor(255, 255, 255)
            doc.setFontSize(12)
            doc.text('Evaluación de Desempeño Docente', printableMarginX + 4, 14)
            doc.setFontSize(10)
            doc.text(`${nombreDocente || 'Docente'}`, printableMarginX + 4, 19)
            if (textoDocumentoDocente) {
                doc.text(textoDocumentoDocente, printableMarginX + 4, 23)
            }
            doc.text(`Período: ${periodoTexto}`, printableMarginX + 4, 27)

            let siguienteY = 40

            const formacionPortadaPdf = [portadaDocente?.anio_de_obtencion, portadaDocente?.institucion_otorgante]
                .filter(Boolean)
                .join(' · ')

            const filasPortadaPdf = [
                ['Título', portadaDocente?.titulo_maximo_obtenido],
                ['Formación', formacionPortadaPdf],
                ['Teléfono celular', portadaDocente?.numero_telefono]
            ].filter(([, valor]) => String(valor || '').trim() !== '')

            const filasSituacionRevistaPdf = [
                ['Fecha de corte', formatearFechaCorte(portadaDocente?.fecha_corte)],
                ['Unidad Académica', portadaDocente?.unidad_academica_nombre],
                ['Planta Básica', portadaDocente?.situacion_de_revista],
                ['Categoría', portadaDocente?.categoria],
                ['Función', portadaDocente?.funcion_nombre]
            ].filter(([, valor]) => String(valor || '').trim() !== '' && valor !== '-')

            const renderSeccionDatosPdf = (titulo, filas) => {
                if (!filas.length) return

                if (siguienteY + 16 > (pageHeight - printableMarginBottom)) {
                    doc.addPage('a4', 'landscape')
                    siguienteY = printableMarginTop + 4
                }

                doc.setFont('helvetica', 'bold')
                doc.setFontSize(10)
                doc.setTextColor(33, 33, 33)
                doc.text(titulo, printableMarginX + 2, siguienteY)
                doc.setFont('helvetica', 'normal')

                doc.autoTable({
                    startY: siguienteY + 2,
                    body: filas,
                    theme: 'grid',
                    tableWidth: contentWidth,
                    rowPageBreak: 'avoid',
                    styles: {
                        fontSize: 8,
                        cellPadding: 1.5,
                        overflow: 'linebreak',
                        textColor: [33, 33, 33],
                        valign: 'top'
                    },
                    margin: { left: printableMarginX, right: printableMarginX },
                    columnStyles: {
                        0: {
                            cellWidth: 54,
                            fontStyle: 'bold',
                            fillColor: [244, 244, 244]
                        },
                        1: {
                            cellWidth: contentWidth - 54
                        }
                    }
                })

                siguienteY = (doc.lastAutoTable?.finalY || siguienteY + 10) + 5
            }

            renderSeccionDatosPdf('Datos de portada', filasPortadaPdf)
            renderSeccionDatosPdf('Situación de revista', filasSituacionRevistaPdf)

            // Tarjetas de puntaje
            if (siguienteY + 22 > (pageHeight - printableMarginBottom)) {
                doc.addPage('a4', 'landscape')
                siguienteY = printableMarginTop + 4
            }

            doc.setTextColor(33, 33, 33)
            doc.setDrawColor(210, 210, 210)
            doc.setFillColor(248, 249, 250)
            const cardsGap = 8
            const cardWidth = (contentWidth - cardsGap) / 2
            const firstCardX = printableMarginX
            const secondCardX = firstCardX + cardWidth + cardsGap
            const cardsY = siguienteY

            doc.roundedRect(firstCardX, cardsY, cardWidth, 18, 2, 2, 'FD')
            doc.roundedRect(secondCardX, cardsY, cardWidth, 18, 2, 2, 'FD')

            doc.setFontSize(9)
            doc.setTextColor(80, 80, 80)
            doc.text('Puntaje a obtener', firstCardX + 4, cardsY + 6)
            doc.text('Puntaje preliminar (solo precalificaciones)', secondCardX + 4, cardsY + 6)
            doc.setFontSize(14)
            doc.setTextColor(33, 33, 33)
            doc.text(String(puntajeObjetivo), firstCardX + 4, cardsY + 14)
            doc.text(String(puntajeObtenido), secondCardX + 4, cardsY + 14)

            siguienteY = cardsY + 25

            gruposOrdenados.forEach((grupo, indexGrupo) => {
                const columnas = [...new Set(
                    grupo.instancias.flatMap((inst) => inst.atributos.map((a) => a.nombre))
                )]

                const normalizarValorPdf = (valor) => String(valor ?? '-')
                    .replace(/\r?\n+/g, ' ')
                    .replace(/\s+/g, ' ')
                    .trim() || '-'

                const head = [columnas]
                const body = grupo.instancias.map((instancia) => {
                    const valoresAtributos = columnas.map((columna) => {
                        const atributo = instancia.atributos.find((a) => a.nombre === columna)
                        return normalizarValorPdf(atributo?.valor)
                    })

                    return valoresAtributos
                })

                doc.setFont('helvetica', 'bold')
                doc.setFontSize(10)
                const tituloGrupo = `${grupo.indiceCompleto} - ${grupo.nombreItem}`
                const lineasTituloGrupo = doc.splitTextToSize(tituloGrupo, contentWidth - 4)
                const alturaTituloGrupo = lineasTituloGrupo.length * 4.2

                if (siguienteY + alturaTituloGrupo + 14 > (pageHeight - printableMarginBottom)) {
                    doc.addPage('a4', 'landscape')
                    siguienteY = printableMarginTop + 4
                }

                doc.text(lineasTituloGrupo, printableMarginX + 2, siguienteY)
                doc.setFont('helvetica', 'normal')

                const anchoPorColumna = contentWidth / Math.max(columnas.length, 1)
                const anchoColumnas = columnas.reduce((acc, _, indexColumna) => {
                    acc[indexColumna] = {
                        cellWidth: anchoPorColumna
                    }

                    return acc
                }, {})

                doc.autoTable({
                    startY: siguienteY + alturaTituloGrupo + 1,
                    head,
                    body,
                    theme: 'grid',
                    tableWidth: contentWidth,
                    horizontalPageBreak: false,
                    rowPageBreak: 'avoid',
                    styles: {
                        fontSize: 7,
                        cellPadding: 1.2,
                        overflow: 'linebreak',
                        valign: 'top'
                    },
                    didParseCell: (data) => {
                        if (data.section !== 'body') return

                        const anchoCelda = anchoColumnas[data.column.index]?.cellWidth
                        if (!anchoCelda) return

                        const valorCelda = Array.isArray(data.cell.raw)
                            ? data.cell.raw.join(' ')
                            : String(data.cell.raw ?? '')

                        data.cell.text = doc.splitTextToSize(valorCelda || '-', Math.max(anchoCelda - 2, 10))
                    },
                    headStyles: {
                        fillColor: [100, 99, 84],
                        textColor: [255, 255, 255],
                        fontStyle: 'bold'
                    },
                    margin: { left: printableMarginX, right: printableMarginX },
                    columnStyles: anchoColumnas
                })

                siguienteY = (doc.lastAutoTable?.finalY || siguienteY + 12) + 6

                if (indexGrupo < gruposOrdenados.length - 1 && siguienteY > (pageHeight - printableMarginBottom)) {
                    doc.addPage('a4', 'landscape')
                    siguienteY = printableMarginTop + 4
                }
            })

            doc.save(construirNombreArchivoPDF())
        } catch (errorExportarPdf) {
            console.log(errorExportarPdf)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo exportar el resumen a PDF.',
                confirmButtonText: 'Cerrar',
                confirmButtonColor: '#972034'
            })
        } finally {
            setExportandoPdf(false)
        }
    }

    return (
        <div
            className={darkMode ? 'div-container-dark-mode' : 'div-container-light-mode'}
            data-bs-theme={darkMode ? 'dark' : 'light'}
            style={styles.page}
        >
            <img src={darkMode ? require('../assets/images/logo_unq_negativo_blanco.png') : require('../assets/images/logo_unq_negativo_negro_rojo.png')} className="unqImage" />
            <div className="toggle-container">
                <label className="toggle-switch">
                    <input type="checkbox" checked={darkMode} onChange={toggleDarkMode} />
                    <span className="slider"></span>
                    <span className="toggle-icons">
                        {darkMode ? '🌙' : '☀️'}
                    </span>
                </label>
            </div>
            <div className="container mt-5 mb-5">
                <div style={styles.pdfContainer}>
                <div style={{ ...styles.hero, ...(darkMode ? styles.heroDark : styles.heroLight) }}>
                    <div>
                        <div style={styles.heroEyebrow}>Resumen de carga</div>
                        <h1 style={styles.heroTitle}>{nombreDocente || 'Docente'}</h1>
                        {textoDocumentoDocente && (
                            <p style={styles.heroSubtitle}>{textoDocumentoDocente}</p>
                        )}
                        {evaluacion && (
                            <p style={styles.heroSubtitle}>
                                Período de evaluación · {formatearPeriodo()}
                            </p>
                        )}
                        {portadaDocente && (
                            <>
                                <div style={styles.portadaResumen}>
                                    {portadaDocente.titulo_maximo_obtenido && (
                                        <div style={styles.portadaItem}>
                                            <span style={styles.portadaLabel}>Título</span>
                                            <span style={styles.portadaValue}>{portadaDocente.titulo_maximo_obtenido}</span>
                                        </div>
                                    )}
                                    {(portadaDocente.anio_de_obtencion || portadaDocente.institucion_otorgante) && (
                                        <div style={styles.portadaItem}>
                                            <span style={styles.portadaLabel}>Formación</span>
                                            <span style={styles.portadaValue}>
                                                {[portadaDocente.anio_de_obtencion, portadaDocente.institucion_otorgante]
                                                    .filter(Boolean)
                                                    .join(' · ')}
                                            </span>
                                        </div>
                                    )}
                                    {portadaDocente.numero_telefono && (
                                        <div style={styles.portadaItem}>
                                            <span style={styles.portadaLabel}>Teléfono celular</span>
                                            <span style={styles.portadaValue}>{portadaDocente.numero_telefono}</span>
                                        </div>
                                    )}
                                </div>

                                <div style={styles.portadaDocumentosBloque}>
                                    <span style={styles.portadaLabel}>Documentos</span>
                                    {Array.isArray(portadaDocente.documentos) && portadaDocente.documentos.length > 0 ? (
                                        <div style={styles.documentosList}>
                                            {portadaDocente.documentos.map((documento) => {
                                                const nombreDocumento = documento?.descripcion || documento?.nombre || documento?.archivo || 'documento'

                                                return (
                                                    <button
                                                        key={documento.id}
                                                        type="button"
                                                        onClick={() => descargarDocumento(documento.id, nombreDocumento)}
                                                        style={styles.documentoLink}
                                                        title={`Descargar ${nombreDocumento}`}
                                                    >
                                                        {nombreDocumento}
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    ) : (
                                        <span style={styles.portadaValue}>Sin documentos</span>
                                    )}
                                </div>

                                {tieneSituacionRevista && (
                                    <div style={styles.situacionRevistaSection}>
                                        <span style={styles.situacionRevistaTitle}>Situación de Revista</span>
                                        <div style={styles.portadaResumen}>
                                            <div style={styles.portadaItem}>
                                                <span style={styles.portadaLabel}>Fecha de corte</span>
                                                <span style={styles.portadaValue}>{formatearFechaCorte(portadaDocente.fecha_corte)}</span>
                                            </div>
                                            <div style={styles.portadaItem}>
                                                <span style={styles.portadaLabel}>Unidad Académica</span>
                                                <span style={styles.portadaValue}>{portadaDocente.unidad_academica_nombre || '-'}</span>
                                            </div>
                                            <div style={styles.portadaItem}>
                                                <span style={styles.portadaLabel}>Planta Básica</span>
                                                <span style={styles.portadaValue}>{portadaDocente.situacion_de_revista || '-'}</span>
                                            </div>
                                            <div style={styles.portadaItem}>
                                                <span style={styles.portadaLabel}>Categoría</span>
                                                <span style={styles.portadaValue}>{portadaDocente.categoria || '-'}</span>
                                            </div>
                                            <div style={styles.portadaItem}>
                                                <span style={styles.portadaLabel}>Función</span>
                                                <span style={styles.portadaValue}>{portadaDocente.funcion_nombre || '-'}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div style={styles.loaderContainer}>
                        <ScaleLoader color='#972034' />
                    </div>
                ) : error ? (
                    <div style={{ ...styles.feedbackCard, ...(darkMode ? styles.cardDark : styles.cardLight) }}>
                        <h2 style={{ ...styles.feedbackTitle, ...(darkMode ? styles.textPrimaryDark : styles.textPrimaryLight) }}>No se pudo cargar el resumen</h2>
                        <p style={{ ...styles.feedbackText, ...(darkMode ? styles.textSecondaryDark : styles.textSecondaryLight) }}>{error}</p>
                    </div>
                ) : (
                    <>
                        {puntajeDocente && (
                            <div style={{ ...styles.puntajeCardStandalone, ...(darkMode ? styles.cardDark : styles.cardLight) }}>
                                <div style={styles.puntajeHeader}>
                                    <span style={styles.puntajeTitleStandaloneWrap}>
                                        <span style={styles.puntajeTitleAccent} />
                                        <span style={{ ...styles.puntajeTitleStandalone, ...(darkMode ? styles.textSecondaryDark : styles.textSecondaryLight) }}>Puntaje del período</span>
                                    </span>
                                    <span style={styles.puntajeRatioStandalone}>
                                        {puntajeDocente.objetivo > 0
                                            ? `${Math.min(100, Math.round((puntajeDocente.obtenido / puntajeDocente.objetivo) * 100))}%`
                                            : '0%'}
                                    </span>
                                </div>
                                <div style={styles.puntajeGrid}>
                                    <div style={styles.puntajeItemStandalone}>
                                        <span style={{ ...styles.puntajeLabelStandalone, ...(darkMode ? styles.textSecondaryDark : styles.textSecondaryLight) }}>Puntaje a obtener</span>
                                        <strong style={{ ...styles.puntajeValueStandalone, ...(darkMode ? styles.textPrimaryDark : styles.textPrimaryLight) }}>{puntajeDocente.objetivo}</strong>
                                    </div>
                                    <div style={styles.puntajeItemStandalone}>
                                        <span style={{ ...styles.puntajeLabelStandalone, ...(darkMode ? styles.textSecondaryDark : styles.textSecondaryLight) }}>Puntaje preliminar (solo precalificaciones)</span>
                                        <strong style={{ ...styles.puntajeValueStandalone, ...(darkMode ? styles.textPrimaryDark : styles.textPrimaryLight) }}>{puntajeDocente.obtenido}</strong>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div style={styles.statsGrid}>
                            <div style={{ ...styles.statCard, ...(darkMode ? styles.cardDark : styles.cardLight) }}>
                                <span style={{ ...styles.statValue, ...(darkMode ? styles.textPrimaryDark : styles.textPrimaryLight) }}>{totalInstancias}</span>
                                <span style={{ ...styles.statLabel, ...(darkMode ? styles.textSecondaryDark : styles.textSecondaryLight) }}>Total Items</span>
                            </div>
                            <div style={{ ...styles.statCard, ...(darkMode ? styles.cardDark : styles.cardLight) }}>
                                <span style={{ ...styles.statValue, color: '#2e8b57' }}>{totalConfirmadas}</span>
                                <span style={{ ...styles.statLabel, ...(darkMode ? styles.textSecondaryDark : styles.textSecondaryLight) }}>Confirmados</span>
                            </div>
                            <div style={{ ...styles.statCard, ...(darkMode ? styles.cardDark : styles.cardLight) }}>
                                <span style={{ ...styles.statValue, color: '#c77d00' }}>{totalPendientes}</span>
                                <span style={{ ...styles.statLabel, ...(darkMode ? styles.textSecondaryDark : styles.textSecondaryLight) }}>Pendientes</span>
                            </div>
                            <div style={{ ...styles.statCard, ...(darkMode ? styles.cardDark : styles.cardLight) }}>
                                <span style={{ ...styles.statValue, ...(darkMode ? styles.textPrimaryDark : styles.textPrimaryLight) }}>{totalItemsDistintos}</span>
                                <span style={{ ...styles.statLabel, ...(darkMode ? styles.textSecondaryDark : styles.textSecondaryLight) }}>Ítems distintos</span>
                            </div>
                        </div>

                        {gruposOrdenados.length === 0 ? (
                            <div style={{ ...styles.feedbackCard, ...(darkMode ? styles.cardDark : styles.cardLight) }}>
                                <h2 style={{ ...styles.feedbackTitle, ...(darkMode ? styles.textPrimaryDark : styles.textPrimaryLight) }}>No hay items cargadas</h2>
                                <p style={{ ...styles.feedbackText, ...(darkMode ? styles.textSecondaryDark : styles.textSecondaryLight) }}>Todavía no se registraron items para este docente.</p>
                            </div>
                        ) : (
                            <>
                                <div style={styles.groupToolbar}>
                                    <button
                                        type="button"
                                        onClick={toggleTodosLosGrupos}
                                        title={estanTodosColapsados ? 'Descolapsar todas las instancias' : 'Colapsar todas las instancias'}
                                        aria-label={estanTodosColapsados ? 'Descolapsar todas las instancias' : 'Colapsar todas las instancias'}
                                        style={{
                                            ...styles.groupToolbarButton,
                                            ...(darkMode ? styles.secondaryButtonDark : styles.secondaryButtonLight)
                                        }}
                                    >
                                        <span style={styles.groupToolbarIcons} aria-hidden="true">
                                            {estanTodosColapsados ? '☰↓' : '☰↑'}
                                        </span>
                                    </button>
                                </div>
                                <div style={styles.groupList}>
                                    {gruposOrdenados.map((grupo) => (
                                        <section
                                            key={grupo.indiceCompleto}
                                            style={{ ...styles.groupCard, ...(darkMode ? styles.cardDark : styles.cardLight) }}
                                        >
                                            <div
                                                style={{ ...styles.groupHeaderButton, ...(darkMode ? styles.groupHeaderButtonDark : styles.groupHeaderButtonLight) }}
                                            >
                                                <div style={styles.groupHeader}>
                                                    <div style={styles.groupHeaderMain}>
                                                        <span style={styles.indexChip}>{grupo.indiceCompleto}</span>
                                                        <h2 style={{ ...styles.instanceTitle, ...(darkMode ? styles.textPrimaryDark : styles.textPrimaryLight) }}>{grupo.nombreItem}</h2>
                                                    </div>
                                                    <div style={styles.groupHeaderAside}>
                                                        <span style={styles.groupCountBadge}>
                                                            {grupo.instancias.length} {grupo.instancias.length === 1 ? 'item' : 'items'}
                                                        </span>
                                                        <span style={{ ...styles.chevron, ...(darkMode ? styles.textSecondaryDark : styles.textSecondaryLight), transform: gruposColapsados[grupo.indiceCompleto] ? 'rotate(-90deg)' : 'rotate(0deg)' }}>
                                                            ▾
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {!gruposColapsados[grupo.indiceCompleto] && (() => {
                                                const columnas = [...new Set(
                                                    grupo.instancias.flatMap((inst) => inst.atributos.map((a) => a.nombre))
                                                )]
                                                return (
                                                    <div style={styles.tableWrapper}>
                                                        <table style={{ ...styles.instanceTable, ...(darkMode ? styles.instanceTableDark : styles.instanceTableLight) }}>
                                                            <thead>
                                                                <tr>
                                                                    {columnas.map((col) => (
                                                                        <th key={col} style={{ ...styles.th, ...(darkMode ? styles.thDark : styles.thLight) }}>{col}</th>
                                                                    ))}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {grupo.instancias.map((instancia) => (
                                                                    <tr key={instancia.id} style={darkMode ? styles.trDark : styles.trLight}>
                                                                        {columnas.map((col) => {
                                                                            const atr = instancia.atributos.find((a) => a.nombre === col)
                                                                            const href = atr?.tipo === 'link' ? normalizarHref(atr?.valor) : ''
                                                                            return (
                                                                                <td key={col} style={{ ...styles.td, ...(darkMode ? styles.textPrimaryDark : styles.textPrimaryLight) }}>
                                                                                    {href
                                                                                        ? (
                                                                                            <a
                                                                                                href={href}
                                                                                                target="_blank"
                                                                                                rel="noopener noreferrer"
                                                                                                style={styles.attrValueText}
                                                                                                title={atr?.valor}
                                                                                            >
                                                                                                {atr?.valor}
                                                                                            </a>
                                                                                        )
                                                                                        : <span style={styles.attrValueText}>{atr?.valor ?? '-'}</span>}
                                                                                </td>
                                                                            )
                                                                        })}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )
                                            })()}
                                        </section>
                                    ))}
                                </div>
                            </>
                        )}

                        {!estadoCargaCerrada && !esOperador && (
                            <div style={styles.actionsRow}>
                                <button
                                    type="button"
                                    onClick={volverAFormulario}
                                    style={{
                                        ...dynamicStyles.logoutButton,
                                        fontSize: 16,
                                        padding: '10px 0',
                                        width: '100%',
                                        flex: 1,
                                        minWidth: '220px',
                                        borderRadius: '10px'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = dynamicStyles.logoutButtonHover.backgroundColor}
                                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = dynamicStyles.logoutButton.backgroundColor}
                                >
                                    Volver al Formulario
                                </button>
                                <button
                                    type="button"
                                    onClick={finalizarCarga}
                                    disabled={finalizandoCarga}
                                    style={{
                                        ...styles.actionButton,
                                        ...styles.primaryButton,
                                        opacity: finalizandoCarga ? 0.7 : 1,
                                        cursor: finalizandoCarga ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {finalizandoCarga ? 'Finalizando...' : 'Finalizar Carga'}
                                </button>
                            </div>
                        )}
                    </>
                )}
                </div>
                {estadoCargaCerrada && !loading && !error && (
                    <div style={styles.exportActionsRow}>
                        <button
                            type="button"
                            onClick={exportarResumenAPDF}
                            disabled={exportandoPdf}
                            style={{
                                ...styles.actionButton,
                                ...styles.primaryButton,
                                minWidth: '220px',
                                opacity: exportandoPdf ? 0.7 : 1,
                                cursor: exportandoPdf ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {exportandoPdf ? 'Exportando PDF...' : 'Exportar a PDF'}
                        </button>
                    </div>
                )}
                {(esOperador || (esAdmin && estadoCargaCerrada)) && !loading && !error && (
                    <div style={styles.exportActionsRow}>
                        <button
                            type="button"
                            onClick={volverAlHome}
                            style={{
                                ...dynamicStyles.logoutButton,
                                ...styles.actionButton,
                                minWidth: '220px'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = dynamicStyles.logoutButtonHover.backgroundColor}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = dynamicStyles.logoutButton.backgroundColor}
                        >
                            Volver
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}

const styles = {
    page: {
        minHeight: '100vh'
    },
    pdfContainer: {
        width: '100%'
    },
    hero: {
        borderRadius: '16px',
        padding: '12px 18px',
        marginBottom: '12px',
        boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12)'
    },
    heroLight: {
        background: 'linear-gradient(135deg, #972034 0%, #c85d4d 100%)',
        color: '#fff'
    },
    heroDark: {
        background: 'linear-gradient(135deg, #5d1320 0%, #8f2d3c 100%)',
        color: '#fff'
    },
    heroEyebrow: {
        fontSize: '10px',
        fontWeight: '700',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        opacity: 0.85,
        marginBottom: '4px'
    },
    heroTitle: {
        margin: 0,
        fontSize: '19px',
        lineHeight: 1.15,
        fontWeight: '800'
    },
    heroSubtitle: {
        margin: '2px 0 0 0',
        fontSize: '12px',
        opacity: 0.9
    },
    puntajeCard: {
        marginTop: '8px',
        borderRadius: '12px',
        padding: '8px 10px',
        background: 'rgba(8, 18, 28, 0.22)',
        border: '1px solid rgba(255, 255, 255, 0.20)',
        backdropFilter: 'blur(4px)'
    },
    puntajeCardStandalone: {
        marginBottom: '12px',
        borderRadius: '14px',
        padding: '10px 12px',
        boxShadow: '0 8px 18px rgba(151, 32, 52, 0.08)',
        border: '1px solid rgba(151, 32, 52, 0.16)',
        background: 'linear-gradient(180deg, rgba(151, 32, 52, 0.05) 0%, rgba(151, 32, 52, 0.02) 100%)'
    },
    puntajeHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '6px'
    },
    puntajeTitleStandaloneWrap: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px'
    },
    puntajeTitleAccent: {
        width: '8px',
        height: '8px',
        borderRadius: '999px',
        backgroundColor: '#972034',
        boxShadow: '0 0 0 4px rgba(151, 32, 52, 0.12)'
    },
    puntajeTitle: {
        fontSize: '10px',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        fontWeight: '800',
        opacity: 0.9
    },
    puntajeTitleStandalone: {
        fontSize: '10px',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        fontWeight: '800',
        opacity: 0.95
    },
    puntajeRatio: {
        fontSize: '11px',
        fontWeight: '800',
        padding: '3px 7px',
        borderRadius: '999px',
        background: 'rgba(255, 255, 255, 0.18)'
    },
    puntajeRatioStandalone: {
        fontSize: '11px',
        fontWeight: '800',
        padding: '4px 8px',
        borderRadius: '999px',
        backgroundColor: 'rgba(151, 32, 52, 0.14)',
        color: '#972034',
        border: '1px solid rgba(151, 32, 52, 0.14)'
    },
    puntajeGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '6px'
    },
    puntajeItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        borderRadius: '10px',
        padding: '7px 9px',
        background: 'rgba(255, 255, 255, 0.12)'
    },
    puntajeItemStandalone: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        borderRadius: '10px',
        padding: '7px 9px',
        backgroundColor: 'rgba(151, 32, 52, 0.08)',
        border: '1px solid rgba(151, 32, 52, 0.08)'
    },
    puntajeLabel: {
        fontSize: '9px',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontWeight: '700',
        opacity: 0.86
    },
    puntajeLabelStandalone: {
        fontSize: '9px',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontWeight: '700',
        opacity: 0.82
    },
    puntajeValue: {
        fontSize: '17px',
        lineHeight: 1.1,
        fontWeight: '900'
    },
    puntajeValueStandalone: {
        fontSize: '17px',
        lineHeight: 1.1,
        fontWeight: '900',
        color: '#7f182b'
    },
    portadaResumen: {
        marginTop: '10px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
        gap: '6px'
    },
    portadaItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        padding: '7px 9px',
        borderRadius: '8px',
        backgroundColor: 'rgba(255, 255, 255, 0.14)',
        backdropFilter: 'blur(4px)',
        minWidth: 0
    },
    portadaDocumentosBloque: {
        marginTop: '6px',
        display: 'flex',
        flexDirection: 'column',
        gap: '3px',
        padding: '7px 9px',
        borderRadius: '8px',
        backgroundColor: 'rgba(255, 255, 255, 0.14)',
        backdropFilter: 'blur(4px)'
    },
    situacionRevistaSection: {
        marginTop: '10px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    situacionRevistaTitle: {
        fontSize: '11px',
        fontWeight: '700',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        opacity: 0.9
    },
    portadaLabel: {
        fontSize: '9px',
        fontWeight: '700',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        opacity: 0.82
    },
    portadaValue: {
        fontSize: '12px',
        fontWeight: '600',
        lineHeight: 1.3,
        wordBreak: 'break-word'
    },
    documentosList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '3px',
        alignItems: 'flex-start'
    },
    documentoLink: {
        background: 'transparent',
        border: 'none',
        padding: 0,
        color: '#ffffff',
        textDecoration: 'underline',
        cursor: 'pointer',
        textAlign: 'left',
        fontSize: '12px',
        lineHeight: 1.3,
        wordBreak: 'break-word'
    },
    loaderContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '240px'
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '10px',
        marginBottom: '12px'
    },
    statCard: {
        borderRadius: '14px',
        padding: '10px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '3px',
        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.07)'
    },
    statValue: {
        fontSize: '22px',
        fontWeight: '800',
        lineHeight: 1
    },
    statLabel: {
        fontSize: '11px',
        fontWeight: '600',
        opacity: 0.75
    },
    groupList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        alignItems: 'stretch'
    },
    groupToolbar: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: '10px'
    },
    groupToolbarButton: {
        border: 'none',
        borderRadius: '10px',
        minWidth: '46px',
        minHeight: '38px',
        padding: '8px 10px',
        fontSize: '12px',
        fontWeight: '700',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    groupToolbarIcons: {
        fontSize: '15px',
        lineHeight: 1,
        letterSpacing: '-1px'
    },
    groupCard: {
        borderRadius: '16px',
        padding: 0,
        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.08)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    },
    groupHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '10px'
    },
    groupHeaderButton: {
        width: '100%',
        border: 'none',
        padding: '10px 14px',
        background: 'transparent',
        textAlign: 'left',
        cursor: 'default'
    },
    groupHeaderButtonLight: {
        color: '#1f2328'
    },
    groupHeaderButtonDark: {
        color: '#f3f4f6'
    },
    groupHeaderMain: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '8px',
        flex: 1,
        minWidth: 0
    },
    groupHeaderAside: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    groupCountBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '6px 10px',
        borderRadius: '999px',
        backgroundColor: 'rgba(151, 32, 52, 0.12)',
        color: '#972034',
        fontSize: '11px',
        fontWeight: '800',
        whiteSpace: 'nowrap'
    },
    chevron: {
        fontSize: '18px',
        fontWeight: '700',
        transition: 'transform 0.2s ease'
    },
    tableWrapper: {
        overflowX: 'auto',
        borderRadius: '0 0 16px 16px'
    },
    instanceTable: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '12px'
    },
    instanceTableLight: {
        color: '#1f2328'
    },
    instanceTableDark: {
        color: '#f3f4f6'
    },
    th: {
        padding: '6px 10px',
        fontWeight: '700',
        fontSize: '10px',
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        textAlign: 'left',
        whiteSpace: 'nowrap'
    },
    thLight: {
        backgroundColor: '#f0eae5',
        color: '#5b6572',
        borderBottom: '1px solid #ece7e2'
    },
    thDark: {
        backgroundColor: '#1e2228',
        color: '#c7ced6',
        borderBottom: '1px solid #3a4048'
    },
    td: {
        padding: '6px 10px',
        verticalAlign: 'middle',
        fontSize: '12px'
    },
    attrValueText: {
        whiteSpace: 'pre-line',
        wordBreak: 'break-word'
    },
    trLight: {
        borderBottom: '1px solid #ece7e2'
    },
    trDark: {
        borderBottom: '1px solid #3a4048'
    },
    cardLight: {
        backgroundColor: '#ffffff',
        border: '1px solid #ece7e2'
    },
    cardDark: {
        backgroundColor: '#23262b',
        border: '1px solid #3a4048'
    },
    textPrimaryLight: {
        color: '#1f2328'
    },
    textPrimaryDark: {
        color: '#f3f4f6'
    },
    textSecondaryLight: {
        color: '#5b6572'
    },
    textSecondaryDark: {
        color: '#c7ced6'
    },

    indexChip: {
        display: 'inline-flex',
        flexShrink: 0,
        padding: '3px 8px',
        borderRadius: '999px',
        fontSize: '10px',
        fontWeight: '800',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        backgroundColor: 'rgba(151, 32, 52, 0.12)',
        color: '#972034'
    },
    instanceTitle: {
        margin: 0,
        fontSize: '14px',
        lineHeight: 1.3,
        fontWeight: '700',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere'
    },
    estadoBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '6px 10px',
        borderRadius: '999px',
        fontSize: '11px',
        fontWeight: '800',
        textTransform: 'capitalize',
        whiteSpace: 'nowrap'
    },
    estadoConfirmado: {
        backgroundColor: '#d9f3e4',
        color: '#146c43'
    },
    estadoConfirmadoAdmin: {
        backgroundColor: '#dbeafe',
        color: '#1e40af'
    },
    estadoPendiente: {
        backgroundColor: '#fff1d6',
        color: '#9a6700'
    },

    feedbackCard: {
        borderRadius: '18px',
        padding: '22px',
        textAlign: 'center',
        boxShadow: '0 16px 36px rgba(0, 0, 0, 0.08)'
    },
    feedbackTitle: {
        margin: 0,
        fontSize: '20px',
        fontWeight: '800'
    },
    feedbackText: {
        margin: '10px 0 0 0',
        fontSize: '13px',
        opacity: 0.75
    },
    actionsRow: {
        marginTop: '20px',
        display: 'flex',
        gap: '10px',
        flexWrap: 'wrap'
    },
    exportActionsRow: {
        marginTop: '18px',
        display: 'flex',
        justifyContent: 'center'
    },
    actionButton: {
        flex: 1,
        minWidth: '220px',
        border: 'none',
        borderRadius: '10px',
        padding: '10px 14px',
        fontSize: '14px',
        fontWeight: '700',
        transition: 'all 0.2s ease'
    },
    primaryButton: {
        backgroundColor: '#2f7d4f',
        color: '#ffffff'
    },
    secondaryButtonLight: {
        backgroundColor: '#ede7e2',
        color: '#3b2a23'
    },
    secondaryButtonDark: {
        backgroundColor: '#343b45',
        color: '#f3f4f6'
    }
}